<script setup lang="ts">
    import { reactive, onMounted, onUnmounted, watch, computed, inject, provide } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import Loading from '@shared/components/Loading.vue';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import Tabs from '@shared/components/Tabs.vue';
    import Tab from '@shared/components/Tab.vue';
    import FeatureLock from '@shared/components/FeatureLock.vue';
    import ModalAiIcon from '@shared/components/ModalAiIcon.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes, bsAlert } from '@shared/components/BsAlerts';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import { sanitizeHtml } from '@shared/utils/DOM';
    import StatisticsNavbar from './components/StatisticsNavbar.vue';
    import StatisticsHeader from './components/StatisticsHeader.vue';
    import QuestionChart from './components/QuestionChart.vue';
    import type { ChartOptionEntry } from './components/questionChartConfig';
    import StatisticsSources from './components/StatisticsSources.vue';
    import StatisticsRespondents from './components/StatisticsRespondents.vue';
    import StatisticsResponses from './components/StatisticsResponses.vue';
    import StatisticsCommunication from './components/StatisticsCommunication.vue';
    import SurveyPageStatistics from '../SurveyPageStatistics/SurveyPageStatistics.vue';
    import StatisticsQuestionSummaries from './components/StatisticsQuestionSummaries.vue';
    import StatisticsDimensions from './components/StatisticsDimensions.vue';
    import FreeTextAnswers from './components/FreeTextAnswers.vue';
    import ConsentAnswers from './components/ConsentAnswers.vue';
    import QuestionAdvancedStatistics from './components/QuestionAdvancedStatistics.vue';
    import QuestionIndicators from './components/QuestionIndicators.vue';
    import QuestionMetaBadges from './components/QuestionMetaBadges.vue';
    import {
        getSurveyStatisticsSummary,
        getSurveyStatistics,
        getSurveyHasDeletedQuestions,
        getCultures,
        getRelatedIssues,
        getQuestionSummaries,
        generateQuestionSummariesAsync,
        getSurveyDimensionStatistics,
        buildQueryString,
        downloadAllQuestionResources,
        type DateRange,
    } from './SurveyStatistics.access';
    import { orderQuestionsWithChildren } from './SurveyStatistics.utils';
    import {
        type SurveyStatisticsSummary,
        type SurveyStatistics,
        type Culture,
        type QuestionFilter,
        type Question,
        type QuestionOption,
        type SurveyStatisticsSource,
        type SurveyCommunicationIssue,
        type QuestionSummaryReport,
        type QuestionSummary,
        type DimensionReport,
        QuestionType,
        QuestionReportType,
    } from '../SurveysTypes.d';
    import { formatNumericText } from '@shared/utils/formatNumericText';
    import { FeatureService } from '@shared/directives/Feature.service';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { ScopeAccess, type vRequireScopeParams } from '@shared/directives/Scope.access';
    import { Permissions } from '@shared/Root/Permissions';

    const props = defineProps<{
        surveyId: string;
    }>();

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);
    const hasContactsReadScope = scopeAccess.hasScope({ Feature: 'Contacts', Permission: Permissions.Read } as vRequireScopeParams);

    const route = useRoute();
    const router = useRouter();

    const respondentsRouteNames = ['SurveyStatisticsRespondents', 'SurveyStatisticsRespondentContact'];
    const responsesRouteNames = [
        'SurveyStatisticsResponses',
        'SurveyStatisticsResponsesForContact',
        'SurveyStatisticsResponse',
        'SurveyStatisticsResponseContact',
    ];
    const showingRespondents = computed(() => respondentsRouteNames.includes(route.name as string));
    const showingResponses = computed(() => responsesRouteNames.includes(route.name as string));
    const showingList = computed(() => showingRespondents.value || showingResponses.value);
    const showingContact = computed(() => route.name === 'SurveyStatisticsRespondentContact' || route.name === 'SurveyStatisticsResponseContact');
    const showingResponseDetail = computed(() => route.name === 'SurveyStatisticsResponse');

    const listBreadcrumbLabel = computed(() => {
        if (showingRespondents.value) {
            return route.query.mode === 'nonrespondents' ? $Resources.StatisticsSurveyNonRespondentsHeading : $Resources.StatisticsSurveyRespondentsHeading;
        }
        if (showingResponses.value) {
            return $Resources.StatisticsSurveyResponsesHeading;
        }
        return '';
    });

    provide(
        'surveyStatisticsQuestions',
        computed(() => state.stats?.Questions ?? [])
    );

    provide(
        'surveyStatisticsTitle',
        computed(() => state.summary?.Title ?? '')
    );

    const nonContentQuestionTypes = [QuestionType.Content, QuestionType.Pagebreak, QuestionType.Divider];

    const state = reactive<{
        summary: SurveyStatisticsSummary | null;
        stats: SurveyStatistics | null;
        isBusy: boolean;
        showEmbedCode: boolean;
        hasDeletedQuestions: boolean;
        showDeletedQuestions: boolean;
        hideUnansweredOptions: boolean;
        dateRange: DateRange;
        cultures: Culture[];
        filteredCultures: Culture[];
        selectedCultures: string[];
        questionFilters: QuestionFilter[];
        autoFilters: QuestionFilter[];
        relatedIssues: SurveyCommunicationIssue[];
    }>({
        summary: null,
        stats: null,
        isBusy: true,
        showEmbedCode: false,
        hasDeletedQuestions: false,
        showDeletedQuestions: false,
        hideUnansweredOptions: true,
        dateRange: { start: null, end: null },
        cultures: [],
        filteredCultures: [],
        selectedCultures: [],
        questionFilters: [],
        autoFilters: [],
        relatedIssues: [],
    });

    function applyAutoFilters() {
        const surveyUserData = contextAccess.state.currentUser?.Data?.Surveys?.find((s) => s.SurveyId === props.surveyId);
        if (surveyUserData?.AutoFilters?.length) {
            state.autoFilters = surveyUserData.AutoFilters;
            state.questionFilters = [...surveyUserData.AutoFilters];
        }
    }

    const connectedIssueCount = computed(() => state.relatedIssues.length);

    const aiTermsLocalStorageKey = 'termsForAiUsageAccepted';
    const aiSummaryEnabled = computed(() => featureAccess.hasFeature('AIAssistant.SurveySummary'));

    const aiState = reactive<{
        termsAccepted: boolean;
        report: QuestionSummaryReport | null;
        summaries: QuestionSummary[];
        loading: boolean;
        generating: boolean;
        generated: boolean;
        show: boolean;
        lastChecked: string | null;
        firstGeneration: boolean;
    }>({
        termsAccepted: localStorage.getItem(aiTermsLocalStorageKey) === 'true',
        report: null,
        summaries: [],
        loading: true,
        generating: false,
        generated: false,
        show: true,
        lastChecked: null,
        firstGeneration: false,
    });

    function acceptAiTerms() {
        aiState.termsAccepted = true;
        localStorage.setItem(aiTermsLocalStorageKey, 'true');
    }

    async function loadQuestionSummaries() {
        if (!aiSummaryEnabled.value) return;

        aiState.loading = true;
        const data = await getQuestionSummaries(props.surveyId);
        aiState.loading = false;
        aiState.report = data;
        if (data) {
            aiState.summaries = data.QuestionSummaries;
            aiState.lastChecked = data.Created;
        }
    }

    let pollQuestionSummariesTimeoutId: ReturnType<typeof setTimeout> | null = null;

    async function pollQuestionSummaries() {
        if (!aiSummaryEnabled.value) return;

        if (!aiState.report) {
            aiState.firstGeneration = true;
        } else if (aiState.lastChecked == null) {
            aiState.lastChecked = aiState.report.Created;
        }

        const data = await getQuestionSummaries(props.surveyId);
        aiState.report = data;
        if (data) {
            aiState.summaries = data.QuestionSummaries;
            if (aiState.lastChecked == null) {
                aiState.lastChecked = data.Created;
            }
        }

        const lastChecked = aiState.lastChecked ? new Date(aiState.lastChecked).getTime() : null;
        const isDone =
            lastChecked != null &&
            aiState.summaries.length > 0 &&
            ((!aiState.firstGeneration && aiState.summaries.every((s) => new Date(s.Created).getTime() > lastChecked)) ||
                (aiState.firstGeneration && aiState.report != null && aiState.report.TotalSummaryQuestions === aiState.summaries.length));

        if (isDone) {
            aiState.generated = true;
            aiState.generating = false;
            aiState.lastChecked = null;
            bsAlert({ title: $Resources.AlertSuccessHeading, content: $Resources.AIAssistantQuestionSummarySuccessBody });
        } else {
            pollQuestionSummariesTimeoutId = setTimeout(pollQuestionSummaries, 5000);
        }
    }

    onUnmounted(() => {
        if (pollQuestionSummariesTimeoutId) clearTimeout(pollQuestionSummariesTimeoutId);
    });

    async function generateQuestionSummaries() {
        if (!aiSummaryEnabled.value || aiState.generating) return;

        aiState.generating = true;

        try {
            await generateQuestionSummariesAsync(props.surveyId, contextAccess.state.currentUser?.Language.Name ?? '');
            pollQuestionSummaries();
        } catch {
            aiState.generating = false;
            bsAlert({ type: AlertTypes.Danger, title: $Resources.AIAssistantErrorHeading, content: $Resources.AIAssistantErrorBody });
        }
    }

    function getQuestionSummaryText(questionId: string): string | null {
        const response = aiState.summaries.find((s) => s.QuestionId === questionId)?.Response;
        return response ? sanitizeHtml(response) : null;
    }

    const dimensionsEnabled = computed(() => featureAccess.hasFeature('Forms.Dimensions') && (state.stats?.Dimensions?.length ?? 0) > 0);

    const dimensionsState = reactive<{ report: DimensionReport | null; loading: boolean }>({
        report: null,
        loading: false,
    });

    let dimensionsActivated = false;

    async function loadDimensionsStatistics() {
        if (!dimensionsActivated) return;

        const qs = buildQueryString(state.dateRange, state.questionFilters, state.selectedCultures);
        dimensionsState.loading = true;
        try {
            dimensionsState.report = await getSurveyDimensionStatistics(props.surveyId, qs);
        } finally {
            dimensionsState.loading = false;
        }
    }

    function activateDimensionsTab() {
        if (dimensionsActivated) return;
        dimensionsActivated = true;
        loadDimensionsStatistics();
    }

    let deletedQuestionsChecked = false;

    const freeTextQuestionTypes = [QuestionType.FreeText, QuestionType.InputField, QuestionType.Hidden];

    async function loadStatistics() {
        const qs = buildQueryString(state.dateRange, state.questionFilters, state.selectedCultures);

        if (!state.summary) {
            state.summary = await getSurveyStatisticsSummary(props.surveyId, qs);
        }

        if (!deletedQuestionsChecked) {
            deletedQuestionsChecked = true;
            state.hasDeletedQuestions = await getSurveyHasDeletedQuestions(props.surveyId);
        }

        state.isBusy = true;
        try {
            state.stats = await getSurveyStatistics(props.surveyId, qs, state.showDeletedQuestions);

            if (state.cultures.length === 0) {
                state.cultures = await getCultures();
            }
            state.filteredCultures = state.cultures.filter((culture) =>
                state.stats!.Cultures.some((surveyCulture) => surveyCulture.CultureCode === culture.CultureCode)
            );
        } finally {
            state.isBusy = false;
        }

        loadQuestionSummaries();
        loadDimensionsStatistics();
    }

    async function loadRelatedIssues() {
        state.relatedIssues = await getRelatedIssues(props.surveyId);
    }

    function onDateRangeChange(start: string | null, end: string | null) {
        if (state.dateRange.start === start && state.dateRange.end === end) return;

        state.dateRange.start = start;
        state.dateRange.end = end;
        loadStatistics();
    }

    function onSelectedCulturesChange(cultures: string[]) {
        state.selectedCultures = cultures;
    }

    watch(() => state.showDeletedQuestions, loadStatistics);
    watch(() => state.selectedCultures, loadStatistics, { deep: true });

    const hasSensitiveAnswers = computed(() => !!state.stats?.Questions.some((question) => question.IsSensitive));

    function toggleQuestionOptionFilter(question: Question, option: QuestionOption) {
        const existingIndex = state.questionFilters.findIndex((f) => f.QuestionId === question.QuestionId && f.QuestionOptionId === option.QuestionOptionId);
        if (existingIndex >= 0) {
            state.questionFilters.splice(existingIndex, 1);
        } else {
            state.questionFilters.push({ QuestionId: question.QuestionId, QuestionOptionId: option.QuestionOptionId });
        }
        loadStatistics();
    }

    function updateQuestionOptionFilters(question: Question, selectedOptionIds: string[]) {
        state.questionFilters = state.questionFilters.filter((f) => !(f.QuestionId === question.QuestionId && f.QuestionOptionId));
        state.questionFilters.push(...selectedOptionIds.map((optionId) => ({ QuestionId: question.QuestionId, QuestionOptionId: optionId })));
        loadStatistics();
    }

    function updateCultureFilter(selectedCultureCodes: string[]) {
        state.selectedCultures = selectedCultureCodes;
    }

    function toggleQuestionValueFilter(questionId: string, value: string) {
        const existingIndex = state.questionFilters.findIndex((f) => f.QuestionId === questionId && f.Value === value);
        if (existingIndex >= 0) {
            state.questionFilters.splice(existingIndex, 1);
        } else {
            state.questionFilters.push({ QuestionId: questionId, Value: value });
        }
        loadStatistics();
    }

    function onToggleRespondentAnswerFilter(value: string) {
        const questionId = typeof route.query.questionId === 'string' ? route.query.questionId : null;
        if (!questionId) return;
        toggleQuestionValueFilter(questionId, value);
    }

    function isAutoFilterEntry(filter: QuestionFilter): boolean {
        return state.autoFilters.some((f) => f.QuestionId === filter.QuestionId && f.QuestionOptionId === filter.QuestionOptionId && f.Value === filter.Value);
    }

    const hasActiveQuestionFilters = computed(() => state.questionFilters.some((f) => !isAutoFilterEntry(f)) || state.selectedCultures.length > 0);

    function resetAllQuestionFilters() {
        state.questionFilters = state.questionFilters.filter((f) => isAutoFilterEntry(f));
        state.selectedCultures = [];
        loadStatistics();
    }

    function getCSATScore(question: Question): number {
        if (!question.ResponseCount) return 0;
        const satisfied = (question.QuestionOptions ?? []).reduce((sum, o) => sum + (o.NumericValue != null && o.NumericValue > 3 ? o.ResponseCount : 0), 0);
        return Math.round((satisfied / question.ResponseCount) * 100);
    }

    const cultureChartOptions = computed<ChartOptionEntry[]>(() =>
        state.filteredCultures.map((culture, index) => {
            const stat = state.stats!.Cultures.find((c) => c.CultureCode === culture.CultureCode);
            return {
                Value: culture.EnglishName || culture.DisplayName,
                Ordinal: index,
                ResponseCount: stat?.ResponseCount ?? 0,
                CultureCode: culture.CultureCode,
            };
        })
    );

    const cultureResponseCount = computed(() => cultureChartOptions.value.reduce((sum, o) => sum + o.ResponseCount, 0));

    function showRespondents() {
        router.push({ name: 'SurveyStatisticsRespondents', params: { surveyId: props.surveyId } });
    }

    function showResponses() {
        router.push({ name: 'SurveyStatisticsResponses', params: { surveyId: props.surveyId } });
    }

    function showNonRespondents() {
        router.push({ name: 'SurveyStatisticsRespondents', params: { surveyId: props.surveyId }, query: { mode: 'nonrespondents' } });
    }

    function showRespondentsForQuestion(questionId: string) {
        router.push({
            name: 'SurveyStatisticsRespondents',
            params: { surveyId: props.surveyId },
            query: { questionId },
        });
    }

    function showRespondentsForQuestionOption(option: ChartOptionEntry) {
        if (!option.QuestionOptionId) return;
        router.push({
            name: 'SurveyStatisticsRespondents',
            params: { surveyId: props.surveyId },
            query: { questionOptionId: option.QuestionOptionId },
        });
    }

    function showRespondentsForCulture(option: ChartOptionEntry) {
        if (!option.CultureCode) return;
        router.push({
            name: 'SurveyStatisticsRespondents',
            params: { surveyId: props.surveyId },
            query: { culture: option.CultureCode },
        });
    }

    function showRespondentsForNpsGroup(questionId: string, group: 'Detractors' | 'Passives' | 'Promoters') {
        router.push({
            name: 'SurveyStatisticsRespondents',
            params: { surveyId: props.surveyId },
            query: { npsGroupQuestionId: questionId, npsGroup: group },
        });
    }

    function showRespondentsFromIssue(source: SurveyStatisticsSource) {
        router.push({
            name: 'SurveyStatisticsRespondents',
            params: { surveyId: props.surveyId },
            query: { issueId: source.IssueId, issueSubject: source.Subject },
        });
    }

    function showNonRespondentsFromIssue(source: SurveyStatisticsSource) {
        router.push({
            name: 'SurveyStatisticsRespondents',
            params: { surveyId: props.surveyId },
            query: { issueId: source.IssueId, issueSubject: source.Subject, mode: 'nonrespondents' },
        });
    }

    const questionEntries = computed(() => {
        if (!state.stats) return [];

        const contentQuestions = state.stats.Questions.filter((q) => !nonContentQuestionTypes.includes(q.Type));
        const orderedQuestions = orderQuestionsWithChildren(contentQuestions);

        let main = 0;
        const subCounters: Record<string, number> = {};

        return orderedQuestions.map((question, index) => {
            let label: string;
            if (question.ParentQuestionId) {
                subCounters[question.ParentQuestionId] = (subCounters[question.ParentQuestionId] || 0) + 1;
                label = `${main}.${subCounters[question.ParentQuestionId]}`;
            } else {
                main++;
                label = `${main}.`;
            }

            const previous = orderedQuestions[index - 1];
            const next = orderedQuestions[index + 1];

            return {
                question,
                label,
                isMatrixHeader: question.Type === QuestionType.Matrix && !question.ParentQuestionId,
                isMatrixChild: !!question.ParentQuestionId,
                isFirstMatrixChild: !!question.ParentQuestionId && previous?.ParentQuestionId !== question.ParentQuestionId,
                isLastMatrixChild: !!question.ParentQuestionId && next?.ParentQuestionId !== question.ParentQuestionId,
            };
        });
    });

    const downloadingQuestionIds = reactive(new Set<string>());

    async function downloadAllFilesForQuestion(questionId: string) {
        if (downloadingQuestionIds.has(questionId)) return;

        downloadingQuestionIds.add(questionId);
        try {
            await downloadAllQuestionResources(questionId);
        } finally {
            downloadingQuestionIds.delete(questionId);
        }
    }

    function getPreviewIframeHeight(event: Event) {
        const el = event.target as HTMLIFrameElement;
        el.contentWindow!.postMessage({ getIframeHeight: { iframeId: el.getAttribute('id') } }, el.src);
    }

    window.addEventListener('message', (event) => {
        if (!event.data.iframeHeight || !event.data.iframeId) return;
        const el = document.getElementById(event.data.iframeId);
        el && (el.style.height = event.data.iframeHeight + 'px');
    });

    onMounted(() => {
        applyAutoFilters();
        loadStatistics();
    });
    onMounted(loadRelatedIssues);

    const newVersionAlertContent = () => {
        const url = `/Surveys/${props.surveyId}/Statistics`;
        return $Resources.GeneralNewVersionAlertContent.replace('{{', `<a href="${url}">`).replace('}}', '</a>');
    };
</script>

<template>
    <BsAlert
        :show="!showingContact"
        class="hidden-print m-0-b"
        :type="AlertTypes.Info"
        :title="$Resources.GeneralNewVersionAlertTitle"
        :content="newVersionAlertContent()" />

    <StatisticsNavbar
        v-if="!showingContact"
        :dateRange="state.dateRange"
        :selectedCultures="state.selectedCultures"
        :showingList="showingList"
        :listLabel="listBreadcrumbLabel"
        :showingDetail="showingResponseDetail"
        :surveyStatus="state.summary?.Status"
        :surveyId="props.surveyId"
        :eventId="state.summary?.EventId"
        :hasSensitiveAnswers="hasSensitiveAnswers"
        :showDeletedQuestions="state.showDeletedQuestions"
        :questionFilters="state.questionFilters"
        :surveyTitle="state.summary?.Title"
        :connectedIssueCount="connectedIssueCount"
        @update:dateRange="onDateRangeChange" />

    <StatisticsRespondents
        v-if="showingRespondents && state.stats && hasContactsReadScope"
        :surveyId="surveyId"
        :dateRange="state.dateRange"
        :questionFilters="state.questionFilters"
        :autoFilters="state.autoFilters"
        :selectedCultures="state.selectedCultures"
        @toggleAnswerFilter="onToggleRespondentAnswerFilter" />

    <StatisticsResponses
        v-else-if="showingResponses && state.stats && hasContactsReadScope"
        :surveyId="surveyId"
        :dateRange="state.dateRange"
        :questionFilters="state.questionFilters"
        :contactId="route.params.contactId as string | undefined"
        :isEventSurvey="!!state.summary?.EventId" />

    <template v-else-if="!showingList">
        <StatisticsHeader
            v-if="state.summary"
            :summary="state.summary"
            :stats="state.stats"
            :hasAdvancedStatistics="featureAccess.hasFeature('Forms.AdvancedStatistics')"
            v-model:showEmbedCode="state.showEmbedCode"
            @show-responses="showResponses"
            @show-respondents="showRespondents" />

        <Tabs v-if="state.stats" teleportTarget="#teleported-target-tabs" navClass="navbar-tabs list-unstyled d-inline-flex">
            <Tab :title="$Resources.StatisticsSurveyQuestionsTab">
                <div class="container-fluid m-lg-t m-xs-b">
                    <div class="hidden-print">
                        <BsAlert :type="AlertTypes.Info" :dismissible="false" :content="$Resources.SurveyHideUnansweredOptionsInfo">
                            <template #content>
                                <FormCheckable
                                    class="m-xs-t"
                                    :label="$Resources.SurveyHideUnansweredOptionsLabel"
                                    :switch-style="SwitchStyle.Standard"
                                    :checked="state.hideUnansweredOptions"
                                    @update:model-value="state.hideUnansweredOptions = $event" />
                            </template>
                        </BsAlert>

                        <BsAlert
                            :show="state.hasDeletedQuestions"
                            :type="AlertTypes.Info"
                            :dismissible="false"
                            :content="$Resources.SurveyHasDeletedQuestionsInfo">
                            <template #content>
                                <FormCheckable
                                    class="m-xs-t"
                                    :label="$Resources.SurveyShowDeletedQuestionsLabel"
                                    :switch-style="SwitchStyle.Standard"
                                    :checked="state.showDeletedQuestions"
                                    @update:model-value="state.showDeletedQuestions = $event" />
                            </template>
                        </BsAlert>

                        <BsAlert
                            :show="hasActiveQuestionFilters"
                            :type="AlertTypes.Info"
                            :dismissible="false"
                            icon="filter"
                            :title="$Resources.SurveyQuestionFilterActiveTitle"
                            :content="$Resources.SurveyQuestionFilterActiveContent"
                            :buttonText="$Resources.SurveyQuestionFilterResetButton"
                            @buttonClick="resetAllQuestionFilters" />

                        <StatisticsQuestionSummaries
                            v-if="aiSummaryEnabled"
                            :report="aiState.report"
                            :hasSummaries="aiState.summaries.length > 0"
                            :loading="aiState.loading"
                            :generating="aiState.generating"
                            :generated="aiState.generated"
                            :termsAccepted="aiState.termsAccepted"
                            v-model:show="aiState.show"
                            @generate="generateQuestionSummaries"
                            @accept-terms="acceptAiTerms" />
                    </div>

                    <article v-if="state.filteredCultures.length > 0" class="question-card">
                        <header>
                            <h2 class="h3 m-sm-t">
                                {{ $Resources.SurveyStatisticsCulturesChartHeading }}
                            </h2>
                        </header>
                        <QuestionChart
                            :options="cultureChartOptions"
                            :type="QuestionType.Single"
                            :response-count="cultureResponseCount"
                            :hide-unanswered-options="state.hideUnansweredOptions"
                            @select-option="showRespondentsForCulture" />

                        <div class="row">
                            <div class="col-md-6">
                                <MultiSelect
                                    class="form-group m-sm-b"
                                    :label="$Resources.SurveyQuestionOptionFilterLabel"
                                    :items="cultureChartOptions"
                                    selectProp="CultureCode"
                                    selectOnly="CultureCode"
                                    displayProp="Value"
                                    :selectedItems="state.selectedCultures"
                                    @update:selectedItems="updateCultureFilter" />
                            </div>
                        </div>
                    </article>

                    <template v-for="entry in questionEntries" :key="entry.question.QuestionId">
                        <h2 v-if="entry.isMatrixHeader" class="h3 m-0-t matrix-group-header">
                            {{ entry.label }} {{ entry.question.Title }}
                            <small class="fw-600">{{ $Resources.QuestionTypeMatrixLabel }}</small>
                            <small v-if="entry.question.Required">* {{ $Resources.QuestionRequiredLabel }}</small>
                        </h2>

                        <article
                            v-else
                            class="question-card"
                            :class="{
                                'matrix-child': entry.isMatrixChild,
                                'matrix-child-first': entry.isFirstMatrixChild,
                                'matrix-child-last': entry.isLastMatrixChild,
                            }">
                            <header>
                                <QuestionMetaBadges :question="entry.question" :answered="entry.question.ResponseCount > 0" />

                                <h2 class="h3 m-0-t">{{ entry.label }} {{ entry.question.Title }}</h2>
                            </header>

                            <div v-if="entry.question.Text" class="m-sm-b" v-html="entry.question.Text"></div>

                            <QuestionIndicators
                                :question="entry.question"
                                :surveyResponseCount="state.stats!.ResponseCount"
                                @showRespondents="showRespondentsForQuestion(entry.question.QuestionId)" />

                            <div
                                v-if="
                                    entry.question.ResponseCount > 0 &&
                                    (entry.question.Type === QuestionType.Single || entry.question.Type === QuestionType.Multiple)
                                ">
                                <QuestionChart
                                    :options="entry.question.QuestionOptions ?? []"
                                    :type="entry.question.Type"
                                    :report-type="entry.question.ReportType"
                                    :response-count="entry.question.ResponseCount"
                                    :total-average-numeric-value="entry.question.TotalAverageNumericValue"
                                    :nps="entry.question.Nps"
                                    :csat-score="entry.question.ReportType === QuestionReportType.Csat ? getCSATScore(entry.question) : undefined"
                                    :hide-unanswered-options="state.hideUnansweredOptions"
                                    @select-option="showRespondentsForQuestionOption"
                                    @select-nps-group="showRespondentsForNpsGroup(entry.question.QuestionId, $event)" />

                                <FeatureLock feature="Forms.AdvancedStatistics" hideWhenLocked>
                                    <QuestionAdvancedStatistics
                                        :surveyId="surveyId"
                                        :question="entry.question"
                                        :questions="state.stats!.Questions"
                                        :dateRange="state.dateRange"
                                        :questionFilters="state.questionFilters"
                                        :selectedCultures="state.selectedCultures"
                                        @update:questionOptionFilters="updateQuestionOptionFilters" />
                                </FeatureLock>
                            </div>

                            <ConsentAnswers
                                v-if="entry.question.ReportType === QuestionReportType.Consent"
                                :question="entry.question"
                                :questionFilters="state.questionFilters"
                                @toggleOptionFilter="toggleQuestionOptionFilter(entry.question, $event)" />

                            <div v-if="entry.question.Type === QuestionType.FileUpload" class="form-group m-sm-b">
                                <button
                                    type="button"
                                    class="btn btn-default btn-sm"
                                    :class="{ 'is-busy': downloadingQuestionIds.has(entry.question.QuestionId) }"
                                    :disabled="downloadingQuestionIds.has(entry.question.QuestionId)"
                                    @click="downloadAllFilesForQuestion(entry.question.QuestionId)">
                                    {{ $Resources.DownloadAllFiles }}
                                </button>
                            </div>

                            <div v-if="freeTextQuestionTypes.includes(entry.question.Type) && entry.question.RespondentCount > 0">
                                <FreeTextAnswers
                                    :surveyId="surveyId"
                                    :question="entry.question"
                                    :dateRange="state.dateRange"
                                    :questionFilters="state.questionFilters"
                                    :autoFilters="state.autoFilters"
                                    :selectedCultures="state.selectedCultures"
                                    @toggleFilterValue="toggleQuestionValueFilter(entry.question.QuestionId, $event)" />
                            </div>

                            <div v-if="aiSummaryEnabled && aiState.show && getQuestionSummaryText(entry.question.QuestionId)" v-show="aiState.termsAccepted">
                                <div class="ai-card m-sm-t m-sm-b">
                                    <h3 class="h4 m-lg-t d-flex align-items-center">
                                        <ModalAiIcon />
                                        <strong>{{ $Resources.AIAssistantQuestionSummary }}</strong>
                                    </h3>

                                    <div class="m-sm-b" v-html="getQuestionSummaryText(entry.question.QuestionId)"></div>
                                </div>
                            </div>
                        </article>
                    </template>
                </div>
            </Tab>

            <Tab v-if="state.summary" :title="$Resources.StatisticsSurveyAppearanceTab">
                <iframe
                    id="survey-preview"
                    :src="state.summary.Url + '?preview=true'"
                    frameborder="0"
                    scrolling="no"
                    loading="lazy"
                    :title="$Resources.PreviewLabel"
                    class="d-block w-full"
                    @load="getPreviewIframeHeight"></iframe>
            </Tab>

            <Tab v-if="featureAccess.hasFeature('Forms.PageStatistics') && state.stats!.PageTimeStats.length > 0" :title="$Resources.SurveyPageViews">
                <div class="container-fluid m-lg-t m-xs-b">
                    <SurveyPageStatistics :pageTimeStats="() => state.stats!.PageTimeStats" />
                </div>
            </Tab>

            <Tab v-if="connectedIssueCount > 0" :title="$Resources.StatisticsSurveyCommunicationTab">
                <StatisticsCommunication :issues="state.relatedIssues" />
            </Tab>

            <Tab v-if="state.stats!.Sources.length > 0" :title="$Resources.StatisticsSurveySourcesTab">
                <StatisticsSources
                    :sources="state.stats!.Sources"
                    @show-non-respondents="showNonRespondents"
                    @show-respondents-from-issue="showRespondentsFromIssue"
                    @show-non-respondents-from-issue="showNonRespondentsFromIssue" />
            </Tab>

            <Tab v-if="dimensionsEnabled" :title="$Resources.QuestionDimensionsHeading" @tab-click="activateDimensionsTab">
                <StatisticsDimensions :report="dimensionsState.report" :loading="dimensionsState.loading" />
            </Tab>
        </Tabs>
    </template>

    <Loading :show="state.isBusy" loadingStyle="progress-bar" fixed></Loading>
</template>

<style lang="less" src="./question-card.less"></style>

